import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

export interface InvitationDto {
  id: number;
  organizationId: number;
  email: string;
  role: string;
  token: string;
  expiresAt: string;
  status: 'Pending' | 'Accepted' | 'Declined' | 'Expired';
  dateCreated: string;
  dateAccepted?: string;
}

export interface InvitationCreateDto {
  organizationId: number;
  email: string;
  role: string;
  expirationDays?: number;
}

export interface InvitationAcceptDto {
  token: string;
  userId?: number;
}

export interface InvitationActionResultDto {
  message?: string;
}

@Injectable({
  providedIn: 'root'
})
export class InvitationService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = '/api/invitations';

  /**
   * Get all invitations (SiteAdmin only)
   */
  getAll(): Observable<InvitationDto[]> {
    return this.http.get<InvitationDto[]>(this.apiUrl);
  }

  /**
   * Get invitation by ID
   */
  getById(id: number): Observable<InvitationDto> {
    return this.http.get<InvitationDto>(`${this.apiUrl}/${id}`);
  }

  /**
   * Get invitations by organization ID (OrgAdmin/SiteAdmin)
   */
  getByOrganizationId(organizationId: number): Observable<InvitationDto[]> {
    return this.http.get<InvitationDto[]>(`${this.apiUrl}/organization/${organizationId}`);
  }

  /**
   * Get invitation by token (public, for acceptance page)
   */
  getByToken(token: string): Observable<InvitationDto> {
    return this.http.get<InvitationDto>(`${this.apiUrl}/token/${token}`);
  }

  /**
   * Create new invitation (OrgAdmin/SiteAdmin)
   */
  create(data: InvitationCreateDto): Observable<InvitationDto> {
    return this.http.post<InvitationDto>(this.apiUrl, data);
  }

  /**
   * Accept invitation (public)
   */
  accept(data: InvitationAcceptDto): Observable<InvitationActionResultDto> {
    return this.http.post<InvitationActionResultDto>(`${this.apiUrl}/accept`, data);
  }

  /**
   * Decline invitation (public)
   */
  decline(token: string): Observable<InvitationActionResultDto> {
    return this.http.post<InvitationActionResultDto>(`${this.apiUrl}/${token}/decline`, {});
  }

  /**
   * Resend invitation with new token (OrgAdmin/SiteAdmin)
   */
  resend(id: number): Observable<InvitationDto> {
    return this.http.post<InvitationDto>(`${this.apiUrl}/${id}/resend`, {});
  }

  /**
   * Delete invitation (OrgAdmin/SiteAdmin)
   */
  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }
}
